/**
 * Evidence Sanitizer - Redacta datos sensibles en evidencias antes de enviar.
 *
 * Trunca a maxLen y reemplaza JWT, Bearer, API keys, emails, tarjetas, cookies
 * por [REDACTED]. Solo para uso en payload de evento; no modifica request real.
 */

const REDACT_PLACEHOLDER = '[REDACTED]';

/**
 * Sanitiza una cadena de evidencia: redacta sensibles y trunca.
 * @param {string} rawEvidence - Cadena cruda (snippet de payload).
 * @param {{ maxLen?: number, redact?: boolean }} options - maxLen truncation; redact enable/disable.
 * @returns {{ sanitized: string, wasRedacted: boolean }}
 */
export function sanitizeEvidence(rawEvidence, options = {}) {
	const maxLen = options.maxLen ?? 256;
	const redact = options.redact !== false;

	if (!rawEvidence || typeof rawEvidence !== 'string') {
		return { sanitized: '', wasRedacted: false };
	}

	let out = rawEvidence;
	let wasRedacted = false;

	if (redact) {
		const before = out;

		// JWT: three base64-like segments separated by dots
		out = out.replace(/eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, REDACT_PLACEHOLDER);

		// Bearer token (word after "Bearer ")
		out = out.replace(/Bearer\s+[A-Za-z0-9_.-]+/gi, 'Bearer ' + REDACT_PLACEHOLDER);

		// Long alphanumeric strings (API keys, tokens) - e.g. 32+ hex/alnum
		out = out.replace(/\b[A-Za-z0-9_-]{32,}\b/g, REDACT_PLACEHOLDER);

		// Email (simple)
		out = out.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, REDACT_PLACEHOLDER);

		// Credit card-like: 4 groups of 4 digits (optional spaces/dashes)
		out = out.replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, REDACT_PLACEHOLDER);

		// Cookie value patterns (name=value; or "cookie: value")
		out = out.replace(/(?:cookie|session|auth|token)\s*[=:]\s*[^\s;&]+/gi, (m) =>
			m.replace(/[=:]\s*\S+/, '=' + REDACT_PLACEHOLDER)
		);

		wasRedacted = (out !== before);
	}

	if (out.length > maxLen) {
		out = out.substring(0, maxLen);
	}

	return { sanitized: out, wasRedacted };
}

/**
 * Sensitive query-param names (case-insensitive). Values are redacted while
 * the parameter name is preserved (so forensics can see "the URL had a token
 * here" without seeing the token itself).
 *
 * Added 2026-05-25 after security review flagged `event.origin_url` and
 * `meta.url` forwarding full customer URLs (potentially containing session
 * tokens / PII / API keys in query strings) verbatim to the IRIS endpoint
 * on every detector hit.
 */
const SENSITIVE_PARAM_NAMES = /^(?:token|access_token|refresh_token|auth|authorization|api[_-]?key|apikey|key|secret|password|passwd|pwd|session|sessionid|sid|jwt|bearer|code|state|nonce|signature|sig|hash|email|phone)$/i;

/**
 * Sanitize a URL for inclusion in event payloads. Strips values of
 * sensitive query parameters (token, session, api_key, etc.) while
 * preserving the parameter name. Truncates the final URL to maxLen.
 * Returns the input unchanged if parsing fails (relative paths, malformed
 * URLs) — but still applies the truncation.
 *
 * @param {string} rawUrl
 * @param {{ maxLen?: number }} options
 * @returns {string}
 */
export function sanitizeUrl(rawUrl, options = {}) {
	const maxLen = options.maxLen ?? 512;
	if (!rawUrl || typeof rawUrl !== 'string') return '';

	let out = rawUrl;
	try {
		const u = new URL(rawUrl);
		// Redact sensitive query params (preserve names so detectors keep context)
		for (const k of Array.from(u.searchParams.keys())) {
			if (SENSITIVE_PARAM_NAMES.test(k)) {
				u.searchParams.set(k, REDACT_PLACEHOLDER);
			}
		}
		// Redact fragment if it looks like a token (JWT-shape or long opaque blob)
		if (u.hash) {
			const frag = u.hash.slice(1);
			if (/^eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(frag) ||
			    /^[A-Za-z0-9_-]{32,}$/.test(frag)) {
				u.hash = '#' + REDACT_PLACEHOLDER;
			}
		}
		// Redact userinfo if present
		if (u.username || u.password) {
			u.username = '';
			u.password = '';
		}
		out = u.toString();
	} catch (e) {
		// Not a parseable URL (relative path, javascript:, etc.) — fall through
		// to truncation. Don't throw — sanitizer must never break detector flow.
	}

	if (out.length > maxLen) {
		// Drop the query string first — it's almost always what makes a URL
		// long, and isn't needed for host-level consumers downstream (backend
		// AdrEvent::parseHostname() only reads scheme+host). This keeps the
		// result a complete, valid URL instead of a truncated fragment with a
		// literal '...' appended, which could fail the backend's `url`
		// validation rule or a downstream re-parse mid percent-encoding.
		try {
			const u2 = new URL(out);
			if (u2.search) {
				u2.search = '';
				out = u2.toString();
			}
		} catch (e) {
			// Not a parseable URL — nothing structured left to trim.
		}
		// Last resort for a pathologically long path/host: plain truncation.
		if (out.length > maxLen) {
			out = out.substring(0, maxLen);
		}
	}
	return out;
}

export default { sanitizeEvidence, sanitizeUrl };
